import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { trackPurchase } from "@/lib/pixels";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const binance = params.get("binance");
  const { clear } = useCart();
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);
  const tracked = useRef(false);

  useEffect(() => {
    if (order && !tracked.current) {
      trackPurchase(order);
      tracked.current = true;
    }
  }, [order]);

  useEffect(() => {
    if (binance) {
      // Binance redirect: trust return, show success (status confirmed via webhook)
      setStatus("paid");
      if (!cleared.current) { clear(); cleared.current = true; }
      return;
    }
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setOrder(data.order);
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (data.status === "expired" || attempts >= 6) { setStatus("error"); return; }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= 6) { setStatus("error"); return; }
        attempts += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, binance]);

  return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center">
      {status === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="w-16 h-16 mx-auto text-drelo-primary animate-spin" />
          <h1 className="font-display font-black text-2xl mt-5 uppercase">Confirmando tu pago...</h1>
          <p className="text-drelo-muted mt-2">Un momento, estamos verificando con la pasarela.</p>
        </div>
      )}
      {status === "paid" && (
        <div className="animate-rise" data-testid="payment-success">
          <div className="w-20 h-20 mx-auto rounded-full bg-green-100 grid place-items-center"><CheckCircle2 className="w-12 h-12 text-drelo-mint" /></div>
          <h1 className="font-display font-black text-3xl mt-5 uppercase">¡Pago confirmado! 🎉</h1>
          <p className="text-drelo-muted mt-2">Gracias por tu compra en Drelo. Te enviaremos los detalles por email.</p>
          {order && (
            <div className="bg-white border rounded-xl p-4 mt-6 text-left">
              <div className="flex justify-between text-sm"><span className="text-drelo-muted">Pedido</span><span className="font-mono font-bold">#{order.id.slice(0, 8)}</span></div>
              <div className="flex justify-between text-sm mt-1"><span className="text-drelo-muted">Total pagado</span><span className="font-black text-drelo-primary">${order.total?.toFixed(2)}</span></div>
            </div>
          )}
          <div className="mt-6 bg-gradient-to-r from-drelo-primary to-drelo-deal text-white rounded-xl p-4 flex items-center gap-3 text-left">
            <Sparkles className="w-8 h-8 shrink-0" />
            <div>
              <p className="font-bold text-sm">¿Quieres ganar dinero con Drelo?</p>
              <Link to="/embajadores" className="text-sm underline font-bold">Únete al programa de embajadores →</Link>
            </div>
          </div>
          <Link to="/tienda" className="mt-6 inline-block bg-drelo-ink text-white font-black uppercase px-7 py-3 rounded-full">Seguir comprando</Link>
        </div>
      )}
      {status === "error" && (
        <div data-testid="payment-error">
          <XCircle className="w-16 h-16 mx-auto text-drelo-urgency" />
          <h1 className="font-display font-black text-2xl mt-5 uppercase">No pudimos confirmar el pago</h1>
          <p className="text-drelo-muted mt-2">Si el cobro se realizó, recibirás un email. Puedes intentarlo de nuevo.</p>
          <Link to="/carrito" className="mt-6 inline-block bg-drelo-primary text-white font-black uppercase px-7 py-3 rounded-full">Volver al carrito</Link>
        </div>
      )}
    </div>
  );
}
